import React from 'react';
import * as ETVConstants from '../ETVConstants';
import {
  Box, Button, Card, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, TextField, Typography
} from '@mui/material';
import { Add, Delete, Edit, Save } from '@mui/icons-material';

class FloorWayfindingEditor extends React.Component {
  state = {
    dialogOpen: false,
    editIndex: -1,

    label: '',
    kind: 'shop',
    x: '',
    y: '',
    keywordInput: '',
    keywords: [],
  };

  openAddDialog = () => {
    this.setState({
      dialogOpen: true,
      editIndex: -1,
      label: '',
      kind: 'shop',
      x: '',
      y: '',
      keywordInput: '',
      keywords: [],
    });
  };

  openEditDialog = (node, index) => {
    this.setState({
      dialogOpen: true,
      editIndex: index,
      label: node.label || '',
      kind: node.kind || 'shop',
      x: node.x ?? '',
      y: node.y ?? '',
      keywordInput: '',
      keywords: Array.isArray(node.keywords) ? node.keywords : [],
    });
  };

  closeDialog = () => {
    this.setState({ dialogOpen: false });
  };

  getNodeStyle = (kind) => {
    switch(kind) {
      case 'shop':
        return {
          bgcolor: '#E3F2FD',
          color: '#1565C0'
        };

      case 'office':
        return {
          bgcolor: '#E8F5E9',
          color: '#2E7D32'
        };

      case 'facility':
        return {
          bgcolor: '#FFF3E0',
          color: '#EF6C00'
        };

      case 'lift':
        return {
          bgcolor: '#F3E5F5',
          color: '#7B1FA2'
        };

      case 'stairs':
        return {
          bgcolor: '#ECEFF1',
          color: '#455A64'
        };

      case 'escalator':
        return {
          bgcolor: '#FCE4EC',
          color: '#C2185B'
        };

      default:
        return {
          bgcolor: '#EEEEEE',
          color: '#616161'
        };
    }
  };  

  addKeyword = () => {
    const keyword = (this.state.keywordInput || '').trim();
    if (!keyword) return;

    if (this.state.keywords.includes(keyword)) {
      this.setState({ keywordInput: '' });
      return;
    }

    this.setState(s => ({
      keywords: [...s.keywords, keyword],
      keywordInput: '',
    }));
  };

  removeKeyword = (idx) => {
    this.setState(s => ({
      keywords: s.keywords.filter((_, i) => i !== idx),
    }));
  };

  handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.addKeyword();
    }
  };

  saveNode = () => {
    const label = (this.state.label || '').trim();
    if (!label) return;

    const node = {
      id: this.state.editIndex >= 0
        ? this.props.nodes[this.state.editIndex].id
        : `node_${Date.now()}`,
      label,
      kind: this.state.kind || 'shop',
      x: Number(this.state.x) || 0,
      y: Number(this.state.y) || 0,
      keywords: this.state.keywords || [],
    };

    const nodes = [...(this.props.nodes || [])];

    if (this.state.editIndex >= 0) {
      nodes[this.state.editIndex] = node;
    } else {
      nodes.push(node);
    }

    this.props.onChange(nodes);
    this.closeDialog();
  };

  removeNode = (idx) => {
    this.props.onChange((this.props.nodes || []).filter((_, i) => i !== idx));
  };

  render() {
    const nodes = this.props.nodes || [];

    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={this.openAddDialog}
          >
            {ETVConstants.trans("add_node")}
          </Button>
        </Box>

        <Box sx={{ display: 'grid', gap: 1 }}>
          {nodes.map((node, idx) => (
            <Card key={node.id || idx} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <Typography variant="subtitle2">
                      {node.label}
                    </Typography>

                    <Chip
                      size="small"
                      label={ETVConstants.trans(node.kind)}
                      sx={{
                        bgcolor: this.getNodeStyle(node.kind).bgcolor,
                        color: this.getNodeStyle(node.kind).color,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {(node.keywords || []).map((kw, kidx) => (
                      <Chip key={`${kw}-${kidx}`} size="small" label={kw} variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => this.openEditDialog(node, idx)}>
                    <Edit fontSize="small" />
                  </IconButton>

                  <IconButton size="small" color="error" onClick={() => this.removeNode(idx)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Card>
          ))}

          {nodes.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No shops/offices added yet.
            </Typography>
          )}
        </Box>

        <Dialog open={this.state.dialogOpen} onClose={this.closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {this.state.editIndex >= 0 ? ETVConstants.trans("update") : ETVConstants.trans("add_node")}
          </DialogTitle>

          <DialogContent dividers>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField
                label="Label"
                fullWidth
                value={this.state.label}
                onChange={(e) => this.setState({ label: e.target.value })}
                placeholder="e.g. Starbucks"
              />

              <TextField
                select
                SelectProps={{ native: true }}
                label="Type"
                fullWidth
                value={this.state.kind}
                onChange={(e) => this.setState({ kind: e.target.value })}
              >
                <option value="shop">{ETVConstants.trans("shop")}</option>
                <option value="office">{ETVConstants.trans("office")}</option>
                <option value="facility">{ETVConstants.trans("facility")}</option>
                <option value="lift">{ETVConstants.trans("lift")}</option>
                <option value="stairs">{ETVConstants.trans("stairs")}</option>
                <option value="escalator">{ETVConstants.trans("escalator")}</option>
              </TextField>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label={ETVConstants.trans("x_coordinate")}
                  type="number"
                  value={this.state.x}
                  onChange={(e) => this.setState({ x: e.target.value })}
                />

                <TextField
                  label={ETVConstants.trans("y_coordinate")}
                  type="number"
                  value={this.state.y}
                  onChange={(e) => this.setState({ y: e.target.value })}
                />
              </Box>

              <TextField
                label={ETVConstants.trans("add_keyword")}
                fullWidth
                value={this.state.keywordInput}
                onChange={(e) => this.setState({ keywordInput: e.target.value })}
                onKeyDown={this.handleKeywordKeyDown}
                placeholder={"Type keyword and press Enter"}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {this.state.keywords.map((kw, idx) => (
                  <Chip
                    key={`${kw}-${idx}`}
                    label={kw}
                    onDelete={() => this.removeKeyword(idx)}
                    variant="outlined"
                  />
                ))}

                {this.state.keywords.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {ETVConstants.trans("no_keywords_yet")}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={this.closeDialog}>Cancel</Button>
            <Button startIcon={<Save />} variant="contained" onClick={this.saveNode}>
              {this.state.editIndex >= 0 ? ETVConstants.trans("update") : ETVConstants.trans("add_node")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default FloorWayfindingEditor;